import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/ui.store'
import type { NavModule } from '@/lib/navigation'

interface NavItemProps {
  module: NavModule
  collapsed?: boolean
}

export function NavItem({ module, collapsed = false }: NavItemProps) {
  const setActiveModule = useUIStore((s) => s.setActiveModule)
  const Icon = module.icon

  return (
    <NavLink
      to={module.path}
      end={module.path === '/'}
      onClick={() => setActiveModule(module.id)}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-2.5 rounded-lg text-sm',
          'transition-all duration-150 ease-out select-none',
          'outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
          collapsed ? 'px-0 py-2 justify-center' : 'px-3 py-2',
          isActive
            ? 'bg-brand-500/10 text-[var(--nav-active-text)]'
            : 'text-[var(--nav-text)] hover:bg-[var(--nav-hover-bg)] hover:text-[var(--nav-hover-text)]',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Active left bar */}
          {isActive && !collapsed && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-full bg-brand-500" />
          )}

          <div className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-150',
            isActive
              ? 'bg-brand-500/15 text-brand-400'
              : 'text-[var(--nav-icon)] group-hover:text-[var(--nav-hover-text)] group-hover:bg-[var(--nav-hover-bg)]'
          )}>
            <Icon size={14} strokeWidth={isActive ? 2.2 : 1.8} />
          </div>

          {!collapsed && (
            <span className={cn(
              'truncate leading-none text-[12.5px] transition-colors',
              isActive ? 'font-semibold' : 'font-medium'
            )}>
              {module.label}
            </span>
          )}

          {/* Tooltip when collapsed */}
          {collapsed && (
            <span className={cn(
              'pointer-events-none absolute left-full ml-3 z-50',
              'rounded-lg border border-[var(--border)] bg-[var(--tooltip-bg)] px-2.5 py-1.5',
              'text-xs text-[var(--tooltip-text)] font-medium whitespace-nowrap',
              'shadow-lg opacity-0 -translate-x-1',
              'group-hover:opacity-100 group-hover:translate-x-0',
              'transition-all duration-150'
            )}>
              {module.label}
            </span>
          )}

          {(module as any).badge && !collapsed && (
            <span className="ml-auto text-[9px] font-bold tracking-wide text-[var(--nav-badge-text)] bg-[var(--nav-badge-bg)] rounded-md px-1.5 py-0.5">
              {(module as any).badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  )
}
